import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Moon, Sun, Plus, Clock, Star, Lock, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SleepPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [sleepLogs, setSleepLogs] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newLog, setNewLog] = useState({
    bedtime: '22:00',
    waketime: '07:00',
    quality: 3,
    notes: ''
  });

  useEffect(() => {
    if (hasAccess) {
      fetchContent();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchContent = async () => {
    try {
      const [logsRes, tipsRes] = await Promise.all([
        axios.get(`${API}/api/sleep/logs`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/sleep/tips`, { headers: getAuthHeader() })
      ]);
      setSleepLogs(logsRes.data);
      setTips(tipsRes.data);
    } catch (error) {
      console.error('Error fetching sleep data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSleepLog = async () => {
    try {
      await axios.post(`${API}/api/sleep/logs`, newLog, { headers: getAuthHeader() });
      setNewLog({ bedtime: '22:00', waketime: '07:00', quality: 3, notes: '' });
      setShowForm(false);
      fetchContent();
    } catch (error) {
      console.error('Error adding sleep log:', error);
    }
  };

  const calculateDuration = (bedtime, waketime) => {
    const [bh, bm] = bedtime.split(':').map(Number);
    const [wh, wm] = waketime.split(':').map(Number);
    let hours = wh - bh;
    let mins = wm - bm;
    if (hours < 0) hours += 24;
    if (mins < 0) {
      mins += 60;
      hours -= 1;
    }
    return `${hours}h ${mins}m`;
  };

  const averageQuality = sleepLogs.length > 0
    ? (sleepLogs.reduce((sum, log) => sum + log.quality, 0) / sleepLogs.length).toFixed(1)
    : 0;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="sleep-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-light">Sömn</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground">Sömnspårning ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full" data-testid="upgrade-btn">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sleep-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-light">Sömn</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Intro */}
        <div className="text-center space-y-3 animate-fade-in">
          <Moon className="w-12 h-12 mx-auto text-primary" />
          <h2 className="font-heading text-3xl font-light">Vila och återhämtning</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            God sömn är grunden för välmående. Följ dina sömnmönster och hitta vägen till bättre vila.
          </p>
        </div>

        {/* Stats */}
        {sleepLogs.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card rounded-2xl border border-border/50 p-6 text-center animate-slide-up">
              <TrendingUp className="w-8 h-8 mx-auto text-primary mb-2" />
              <p className="text-2xl font-heading font-medium">{averageQuality}</p>
              <p className="text-sm text-muted-foreground">Snitt kvalitet</p>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-6 text-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Moon className="w-8 h-8 mx-auto text-primary mb-2" />
              <p className="text-2xl font-heading font-medium">{sleepLogs.length}</p>
              <p className="text-sm text-muted-foreground">Loggade nätter</p>
            </div>
          </div>
        )}

        {/* Add Log Button */}
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="w-full rounded-xl py-6"
            variant="outline"
            data-testid="add-sleep-toggle"
          >
            <Plus className="w-5 h-5 mr-2" />
            Logga sömn
          </Button>
        )}

        {/* Add Log Form */}
        {showForm && (
          <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-6 animate-fade-in">
            <h3 className="font-heading text-lg font-medium">Hur sov du i natt?</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Moon className="w-4 h-4 inline mr-1" />
                  Lade mig
                </label>
                <input
                  type="time"
                  value={newLog.bedtime}
                  onChange={(e) => setNewLog({ ...newLog, bedtime: e.target.value })}
                  className="w-full p-3 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="bedtime-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Sun className="w-4 h-4 inline mr-1" />
                  Vaknade
                </label>
                <input
                  type="time"
                  value={newLog.waketime}
                  onChange={(e) => setNewLog({ ...newLog, waketime: e.target.value })}
                  className="w-full p-3 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="waketime-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-3">Sömnkvalitet</label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setNewLog({ ...newLog, quality: rating })}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      newLog.quality >= rating
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                    data-testid={`quality-${rating}`}
                  >
                    <Star className={`w-5 h-5 ${newLog.quality >= rating ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Anteckningar (valfritt)</label>
              <textarea
                value={newLog.notes}
                onChange={(e) => setNewLog({ ...newLog, notes: e.target.value })}
                placeholder="T.ex. vaknade flera gånger, drömde mycket..."
                className="w-full p-4 rounded-xl border border-border/50 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={2}
                data-testid="sleep-notes-input"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={addSleepLog} className="flex-1 rounded-xl" data-testid="save-sleep-btn">
                Spara
              </Button>
              <Button onClick={() => setShowForm(false)} variant="outline" className="rounded-xl">
                Avbryt
              </Button>
            </div>
          </div>
        )}

        {/* Sleep Logs */}
        <section className="space-y-4">
          <h3 className="font-heading text-xl font-medium">Sömnhistorik</h3>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-secondary/50 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : sleepLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Ingen sömn loggad än. Börja med att logga din första natt!
            </p>
          ) : (
            <div className="space-y-3">
              {sleepLogs.map((log, index) => (
                <div
                  key={log.id}
                  className="bg-card rounded-2xl border border-border/50 p-4 animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`sleep-log-${log.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(log.created_at).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'short' })}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="flex items-center gap-1 text-sm">
                          <Moon className="w-4 h-4" />
                          {log.bedtime}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="flex items-center gap-1 text-sm">
                          <Sun className="w-4 h-4" />
                          {log.waketime}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {calculateDuration(log.bedtime, log.waketime)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${
                            star <= log.quality ? 'text-primary fill-primary' : 'text-muted-foreground/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  {log.notes && (
                    <p className="text-sm text-muted-foreground mt-2 italic">{log.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sleep Tips */}
        <section className="space-y-4">
          <h3 className="font-heading text-xl font-medium">Tips för bättre sömn</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {tips.map((tip, index) => (
              <div
                key={tip.id}
                className="bg-card rounded-2xl border border-border/50 p-5 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`sleep-tip-${tip.id}`}
              >
                <h4 className="font-heading font-medium">{tip.title}</h4>
                <p className="text-sm text-muted-foreground mt-2">{tip.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-xs text-muted-foreground">
            Sömn är kroppens sätt att läka. Ge dig själv den vila du förtjänar.
          </p>
        </div>
      </main>
    </div>
  );
}
